import { useState } from "react";
import { styles } from "../../styles";
import { ErrorNote } from "../common";
import { useShop } from "../../context/ShopContext";
import { useToast } from "../../context/ToastContext";
import { addBreed, updateBreed, retireBreed, reactivateBreed } from "../../data/breeds";
import { logAudit } from "../../data/audit";
import type { Breed } from "../../lib/types";

// One editable row. Saves a field on blur so admin edits take effect live, with
// no redeploy. Values are clamped to sane minimums.
function BreedRow({ breed, onError }: { breed: Breed; onError: (m: string) => void }) {
  const notify = useToast();
  const [name, setName] = useState(breed.name);
  const [minutes, setMinutes] = useState(String(breed.appointmentMinutes));
  const [max, setMax] = useState(String(breed.dailyMax));

  const commit = async (patch: Partial<Omit<Breed, "id">>) => {
    try {
      await updateBreed(breed.id, patch);
      void logAudit(`Updated breed ${breed.name}`);
      notify("Breed updated.");
    } catch {
      onError("We could not update that breed. Please try again.");
    }
  };

  const commitName = () => {
    const v = name.trim();
    if (!v || v === breed.name) { setName(breed.name); return; }
    void commit({ name: v });
  };
  const commitMinutes = () => {
    const v = Math.max(5, Math.round(Number(minutes) || 0));
    setMinutes(String(v));
    if (v !== breed.appointmentMinutes) void commit({ appointmentMinutes: v });
  };
  const commitMax = () => {
    const v = Math.max(0, Math.round(Number(max) || 0));
    setMax(String(v));
    if (v !== breed.dailyMax) void commit({ dailyMax: v });
  };

  return (
    <div style={{ ...styles.breedItem, opacity: breed.active ? 1 : 0.55 }}>
      <input
        value={name} onChange={(e) => setName(e.target.value)} onBlur={commitName}
        className="breedName" style={styles.breedName} aria-label={`Breed name for ${breed.name}`}
      />
      <div>
        <span style={styles.numLabel}>min</span>
        <input
          value={minutes} onChange={(e) => setMinutes(e.target.value)} onBlur={commitMinutes}
          inputMode="numeric" style={styles.numInput} aria-label={`Appointment minutes for ${breed.name}`}
        />
      </div>
      <div>
        <span style={styles.numLabel}>max/day</span>
        <input
          value={max} onChange={(e) => setMax(e.target.value)} onBlur={commitMax}
          inputMode="numeric" style={styles.numInput} aria-label={`Daily max for ${breed.name}`}
        />
      </div>
      {breed.active ? (
        <button
          className="link" style={styles.removeBtn}
          onClick={() => { void retireBreed(breed.id); void logAudit(`Retired breed ${breed.name}`); }}
          aria-label={`Retire ${breed.name}`}
        >Retire</button>
      ) : (
        <button
          className="link" style={{ ...styles.removeBtn, color: "var(--accent)" }}
          onClick={() => { void reactivateBreed(breed.id); void logAudit(`Restored breed ${breed.name}`); }}
          aria-label={`Restore ${breed.name}`}
        >Restore</button>
      )}
    </div>
  );
}

export function BreedsCard() {
  const { breeds } = useShop();
  const notify = useToast();
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newMin, setNewMin] = useState("60");
  const [newMax, setNewMax] = useState("3");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    setError(null);
    const name = newName.trim();
    if (!name) { setError("Give the breed a name."); return; }
    if (breeds.some((b) => b.name.toLowerCase() === name.toLowerCase())) {
      setError("That breed already exists.");
      return;
    }
    setBusy(true);
    try {
      await addBreed({
        name,
        appointmentMinutes: Math.max(5, Math.round(Number(newMin) || 60)),
        dailyMax: Math.max(0, Math.round(Number(newMax) || 1)),
        active: true,
      });
      void logAudit(`Added breed ${name}`);
      setNewName(""); setNewMin("60"); setNewMax("3");
      notify("Breed added.");
    } catch {
      setError("We could not add that breed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.card}>
      <h2 style={styles.cardTitle}>Breeds &amp; capacity</h2>
      <p style={styles.sub}>
        Each breed sets its own appointment length and how many can book per day. Lower the daily
        max to protect a solo groomer; raise it as you add help. Changes apply right away.
      </p>
      {error && <ErrorNote>{error}</ErrorNote>}
      <div style={styles.breedTable}>
        {breeds.map((b) => <BreedRow key={b.id} breed={b} onError={setError} />)}
      </div>

      <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--line)" }}>
        <span style={styles.label}>Add a breed</span>
        <div style={styles.addRow}>
          <input
            value={newName} onChange={(e) => setNewName(e.target.value)}
            placeholder="Breed name" className="input" style={{ ...styles.input, flex: 1, minWidth: 140 }}
            aria-label="New breed name"
          />
          <input
            value={newMin} onChange={(e) => setNewMin(e.target.value)}
            inputMode="numeric" style={styles.numInput} aria-label="New breed appointment minutes" title="Minutes"
          />
          <input
            value={newMax} onChange={(e) => setNewMax(e.target.value)}
            inputMode="numeric" style={styles.numInput} aria-label="New breed daily max" title="Daily max"
          />
          <button className="primary" style={styles.primarySm} onClick={add} disabled={busy}>
            {busy ? "Adding…" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}
